import { useEffect, useMemo, useState } from "react";
import { isSearchableQuery } from "@playbookai/types";
import { usePeopleSource } from "./user-directory.js";

/** Search account owners without assigning them or changing resource access. */
export function useAccountOwnerOptions(ownerIds: readonly string[]) {
  const people = usePeopleSource();
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    if (people?.resolve && ownerIds.length > 0)
      void people
        .resolve(ownerIds)
        .then((rows) => {
          if (!cancelled)
            setNames((current) => ({
              ...current,
              ...Object.fromEntries(rows.map((row) => [row.id, row.name])),
            }));
        })
        .catch(() => {
          // Keep unavailable saved selections removable.
        });
    return () => {
      cancelled = true;
    };
  }, [people, ownerIds]);
  const source = useMemo(
    () => ({
      kind: "remote" as const,
      searchable: true,
      searchPlaceholder: "Search owners",
      emptyMessage:
        "No matching owners. Search names using at least 2 characters.",
      load: async ({
        query,
        cursor,
        signal,
      }: {
        query: string;
        cursor?: string;
        signal: AbortSignal;
      }) => {
        if (!people || !isSearchableQuery(query))
          return { options: [], nextCursor: null };
        const result = await people.search(query, {
          ...(cursor ? { cursor } : {}),
          signal,
        });
        if (!signal.aborted)
          setNames((current) => ({
            ...current,
            ...Object.fromEntries(
              result.items.map((person) => [person.id, person.name]),
            ),
          }));
        return {
          options: result.items.map((person) => ({
            value: person.id,
            label: person.name,
            person: { email: person.email ?? null },
          })),
          nextCursor: result.nextCursor,
        };
      },
    }),
    [people],
  );
  return {
    available: people !== null,
    source,
    selectedLabel: (id: string) => names[id] ?? "Unavailable owner",
  };
}
